import { useState, FC, CSSProperties } from 'react';
import { CSSTransition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';

interface AccordionItemProps {
  title: string;
  desc: {
    text: string;
    points?: string[];
    pointsType?: 'a' | 'i' | '1' | 'A' | 'I';
  }[];
  textType?: 'a' | 'i' | '1' | 'A' | 'I';
}

const AccordionItem: FC<AccordionItemProps> = ({ title, desc, textType }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const styles = {
    accordionItem: {
      borderBottom: '1px solid #ddd',
    } as CSSProperties,
    accordionTitle: {
      width: '100%',
      padding: '15px',
      textAlign: 'left' as const,
      background: '#f7f7f7',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'background 0.3s ease',
      borderRadius:"12px"
    } as CSSProperties,
    accordionTitleHover: {
      background: '#e2e2e2',
    },
    accordionContent: {
      overflow: 'hidden',
      transition: 'max-height 0.5s ease',
      paddingTop:"8px",
      paddingLeft:"4px"
    } as CSSProperties,
    accordionContentEntering: {
      maxHeight: '500px',
    } as CSSProperties,
    accordionContentExiting: {
      maxHeight: '0',
    } as CSSProperties,
    icon: {
      fontSize: '20px',
      transition: 'transform 0.3s ease',
    } as CSSProperties,
    iconOpen: {
      transform: 'rotate(45deg)',
    } as CSSProperties,
    iconClosed: {
      transform: 'rotate(0deg)',
    } as CSSProperties,
  };

  const getAccordionContentStyle = (state: TransitionStatus): CSSProperties => {
    switch (state) {
      case 'entering':
      case 'entered':
        return styles.accordionContentEntering;
      case 'exiting':
      case 'exited':
        return styles.accordionContentExiting;
      default:
        return {};
    }
  };

  const renderList = (points: string[], pointsType: 'a' | 'i' | '1' | 'A' | 'I') => (
    <ol type={pointsType}>
      {points.map((point, idx) => (
        <li key={idx}>{point}</li>
      ))}
    </ol>
  );

  return (
    <div style={styles.accordionItem}>
      <button
        style={styles.accordionTitle}
        onClick={toggleAccordion}
        onMouseEnter={(e) => (e.currentTarget.style.background = styles.accordionTitleHover.background as string)}
        onMouseLeave={(e) => (e.currentTarget.style.background = styles.accordionTitle.background as string)}
      >
        {title}
        <span style={{ ...styles.icon, ...(isOpen ? styles.iconOpen : styles.iconClosed) }}>
          +
        </span>
      </button>
      <CSSTransition in={isOpen} timeout={500} classNames="accordion" unmountOnExit>
        {(state) => (
          <div style={{ ...styles.accordionContent, ...getAccordionContentStyle(state) }}>
            {desc.map((d, idx) => (
              <div key={idx}>
                <p>{d.text}</p>
                {d.points && renderList(d.points, d.pointsType || textType || '1')}
              </div>
            ))}
          </div>
        )}
      </CSSTransition>
    </div>
  );
};

interface AccordionProps {
  items: {
    title: string;
    desc: {
      text: string;
      points?: string[];
      pointsType?: 'a' | 'i' | '1' | 'A' | 'I';
    }[];
    textType?: 'a' | 'i' | '1' | 'A' | 'I';
  }[];
}

const Accordion: FC<AccordionProps> = ({ items }) => {
  return (
    <div style={{ width: '100%', borderRadius: '4px' , padding:"12px 12px 0 12px"}}>
      {items.map((item, index) => (
        <AccordionItem key={index} {...item} />
      ))}
    </div>
  );
};

export default Accordion;
